import { Node, useReactFlow } from '@xyflow/react';
import { useMemo, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'src/components/button';
import FormSwitch from 'src/components/form-switch';
import Radio from 'src/components/radio';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { exportAutoConfiguration } from 'src/utils/export-auto-configuration';
import { isValidSubnetAddress } from 'src/utils/ip';
import { getInfoConnected } from 'src/utils/node';
import { twMerge } from 'tailwind-merge';

import { EDataType, NodeCategoryModel } from '../../models/node-data';
import { globalSettingsDefault } from '../global-settings';
import WizardExportAutoConfiguration from './wizard-export-auto-configuration';

interface AutoTabProps {
  allNodeSwitches: Node[];
  wizardExport: boolean;
  setWizardExport: (wizardExport: boolean) => void;
}

export function AutoTab({
  allNodeSwitches,
  wizardExport,
  setWizardExport,
}: AutoTabProps) {
  const { vlans, projectData, globalSettings } = useGlobalContext();
  const { getNodes, getEdges } = useReactFlow();
  const [selectedNode, setSelectedNode] = useState<string>('');
  const nodes = getNodes();
  const edges = getEdges();

  const [configurationName, setConfigurationName] = useState<
    Record<string, string>
  >({});

  const exportZipBundle = async () => {
    if (!selectedNode) {
      toast.error('No switches selected');
      return;
    }
    try {
      await exportAutoConfiguration({
        nodes,
        edges,
        vlans,
        targets: allNodeSwitches.map((node) => ({
          id: node.id,
          name: configurationName[node.id],
        })),
        selectedNode,
        projectName: projectData?.projectName || 'deployment',
        globalSettings: globalSettings ?? globalSettingsDefault,
      });
      toast.success('Zip bundle exported successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('exportZipBundle ~ error:', error);
      toast.error('Failed to export zip bundle');
    }
  };

  useEffect(() => {
    setSelectedNode('');
    setConfigurationName(
      allNodeSwitches.reduce(
        (acc, node) => {
          acc[node.id as string] = (
            node.data?.category as NodeCategoryModel
          )?.name;
          return acc;
        },
        {} as Record<string, string>,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorAutoConfiguration = useMemo(() => {
    if (vlans.length === 0) return 'No VLANs found';
    if (vlans.some((vlan) => vlan.vlanId.toString() === '3579'))
      return 'VLAN 3579 is not allowed';

    if (
      vlans.some(
        (vlan) =>
          (vlan.ipSubnet === '172.31.255.0' || vlan.ip === '172.31.255.0') &&
          vlan.subnetMask === '255.255.255.0',
      )
    )
      return 'IP subnet 172.31.255.0 with mask 255.255.255.0 is used';

    if (
      nodes.some(
        (node) =>
          (((node?.data?.category as NodeCategoryModel)?.ip ===
            '172.31.255.0' ||
            (node?.data?.category as NodeCategoryModel)?.subnet ===
              '172.31.255.0') &&
            (node?.data?.category as NodeCategoryModel)?.subnetMask ===
              '255.255.255.0') ||
          (node?.data?.category as NodeCategoryModel)?.ipInterfaces?.some(
            (ip) =>
              (ip.ip === '172.31.255.0' || ip.subnet === '172.31.255.0') &&
              ip.subnetMask === '255.255.255.0',
          ),
      )
    )
      return 'IP 172.31.255.0 is used';

    if (allNodeSwitches.length >= 250)
      return 'Less than 250 ROQSTAR managed switches';

    const allDeviceNames = nodes.map(
      (node) => (node.data?.category as NodeCategoryModel)?.name,
    );
    if (allDeviceNames.some((name) => !name)) return 'Device name is empty';
    if (
      allDeviceNames.some(
        (name) => allDeviceNames.filter((n) => n === name).length > 1,
      )
    )
      return 'Device name is not unique';

    const allVlans = vlans.map((vlan) => vlan.vlanId);

    if (allVlans.some((vlan) => !vlan)) return 'VLAN ID is empty';
    if (allVlans.some((vlan) => allVlans.filter((n) => n === vlan).length > 1))
      return 'VLAN ID is not unique';

    const allEdges = edges.map(
      (edge) => (edge.data?.vlanIds as number[]) || [],
    );

    if (allEdges.some((vlanIds) => vlanIds.length === 0))
      return 'Some edge has no vlan';

    const allIpInterfaces = nodes.map(
      (node) => (node.data?.category as NodeCategoryModel)?.ipInterfaces || [],
    );

    const allIpAddressInterfaces = allIpInterfaces
      .flat()
      .filter((item) => item.ip);

    if (
      allIpAddressInterfaces.some(
        (ipInterface) =>
          allIpAddressInterfaces.filter((n) => n.ip === ipInterface.ip).length >
            1 ||
          !isValidSubnetAddress(ipInterface.subnet, ipInterface.subnetMask),
      )
    )
      return 'Ip address is not unique';

    const allNodesPoE = nodes.filter(
      (node) =>
        (node.data?.category as NodeCategoryModel)?.type === EDataType.Device &&
        (node.data?.category as NodeCategoryModel)?.poe?.enable,
    );

    if (allNodesPoE.length > 0) {
      if (
        allNodesPoE.some((node) => {
          if (!(node.data?.category as NodeCategoryModel)?.ports?.[0]?.id)
            return false;
          const infoConnected = getInfoConnected(
            node.id,
            (node.data?.category as NodeCategoryModel)?.ports?.[0]?.id || '',
            edges,
            nodes,
          );

          return !infoConnected.port?.isPoE;
        })
      ) {
        return 'Device is not connected to PoE port';
      }
    }
    return '';
  }, [vlans, nodes, edges, allNodeSwitches.length]);

  return (
    <>
      <span className="ml-2 py-1 text-sm text-gray-700">
        Starting point for configuration deployment (USB connection):
      </span>
      <div className="relative max-h-96 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300 overflow-y-auto">
          <tbody className="bg-white">
            {allNodeSwitches.map((node) => (
              <tr key={node.id} className="even:bg-gray-50">
                <td className="whitespace-nowrap py-2 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                  <div className="flex flex-row items-center justify-start gap-2">
                    <Radio
                      checked={selectedNode === node.id}
                      id={`switch-${node.id}`}
                      name={`switch-${node.id}`}
                      data-testid={`dialog-package-radio-${node.id}`}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedNode(node.id);
                        } else {
                          setSelectedNode('');
                        }
                      }}
                    />
                  </div>
                </td>
                <td
                  className={twMerge([
                    'whitespace-nowrap px-3 py-2 text-sm text-gray-900',
                    (node.data?.category as NodeCategoryModel)?.isValid
                      ? ''
                      : 'text-red-500',
                  ])}
                >
                  <label
                    className="cursor-pointer"
                    htmlFor={`switch-${node.id}`}
                    data-testid={`dialog-package-label-${node.id}`}
                  >
                    {(node.data?.category as NodeCategoryModel)?.name} (
                    {node.data?.key as string})
                  </label>
                </td>
                <td className="relative whitespace-nowrap py-2 pl-3 pr-4 text-right text-sm font-medium sm:pr-3" />
              </tr>
            ))}
            {allNodeSwitches.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-sm text-gray-700">
                  No switches found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div>
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-0 whitespace-nowrap text-sm/6 font-medium text-gray-900" />
          <div className="flex w-full justify-between text-sm text-gray-700 sm:mt-0">
            <div className="flex flex-col items-start justify-center gap-1">
              <FormSwitch
                id="dialog-package-export-multiple-file-with-variable-switch"
                label="Multiple file with variable"
                checked={wizardExport}
                size="sm"
                data-testid="dialog-package-export-multiple-file-with-variable-switch"
                wrapperClassName="flex-row-reverse"
                onChange={() => setWizardExport(!wizardExport)}
              />
            </div>
            {!wizardExport && (
              <div className="flex flex-col items-end justify-center gap-1">
                {errorAutoConfiguration && (
                  <span className="text-red-500">{errorAutoConfiguration}</span>
                )}
                <Button
                  type="button"
                  data-testid="dialog-package-export-zip-bundle-button"
                  onClick={() => {
                    exportZipBundle();
                  }}
                  disabled={!!errorAutoConfiguration}
                >
                  Export zip bundle
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      {wizardExport && (
        <WizardExportAutoConfiguration
          selectedNode={selectedNode}
          errorAutoConfiguration={errorAutoConfiguration}
          targets={allNodeSwitches.map((node) => ({
            id: node.id,
            name: configurationName[node.id],
          }))}
        />
      )}
    </>
  );
}
